import React, { useEffect, useMemo, useState } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { FaInstagram } from "react-icons/fa";

const GlobalStyle = createGlobalStyle`
  body {
    font-family: 'Helvetica', sans-serif;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
`;

type HolidayMode =
  | "none"
  | "newyear" // 1월1일
  | "lunarnewyear" // 설날(음력)
  | "thanksgiving" // 추수감사절
  | "christmas"; // 크리스마스

type YearMode = "y2025" | "y2026plus";

const Container = styled.div<{ holidayMode: HolidayMode }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;

  /* ✅ 기본은 항상 검정 */
  background-color: #000;
  color: #fff;

  padding: 20px;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;

  /* ✅ 명절별로 은은한 그라데이션(원하면 여기 더 줄여도 됨) */
  background-image: ${({ holidayMode }) =>
    holidayMode === "christmas"
      ? "radial-gradient(circle at top, #234f32, #000000)"
      : holidayMode === "lunarnewyear"
      ? "radial-gradient(circle at top, #4a0a0a, #000000)"
      : holidayMode === "thanksgiving"
      ? "radial-gradient(circle at top, #3a240f, #000000)"
      : "none"};

  transition: background-image 0.6s ease;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 3em;
  margin-bottom: 20px;
  text-align: center;
  text-transform: uppercase;
`;

const Title2 = styled.h1`
  font-size: 2em;
  margin-bottom: 20px;
  text-align: center;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 1em;
  }
`;

const DateTime = styled.div`
  font-size: 1.5em;
  margin-bottom: 10px;
  font-weight: bold;
  text-align: center;
  text-transform: uppercase;
`;

const ProgressContainer = styled.div`
  width: 80%;
  max-width: 600px;
  background-color: #444;
  border-radius: 25px;
  overflow: hidden;
  position: relative;
  height: 20px;
  margin-bottom: 30px;
`;

const ProgressBar = styled.div<{ width: string; color: string }>`
  height: 100%;
  background-color: ${({ color }) => color};
  width: ${(props) => props.width};
  transition: width 1s ease;
  position: absolute;
  top: 0;
  left: 0;
`;

const ProgressText = styled.span`
  position: absolute;
  width: 100%;
  text-align: center;
  font-weight: bold;
  text-transform: uppercase;
  z-index: 1;
`;

const InstagramLink = styled.a`
  display: flex;
  align-items: center;
  margin-top: 20px;
  color: #fff;
  text-decoration: none;
  font-size: 1.2em;

  &:hover {
    color: #e84d19;
  }
`;

const InstagramIcon = styled(FaInstagram)`
  margin-right: 10px;
`;

const ResponsiveStyle = createGlobalStyle`
  @media (max-width: 768px) {
    ${Title} {
      font-size: 1.5em;
    }
    ${DateTime} {
      font-size: 1.2em;
    }
    ${ProgressText} {
      font-size: 0.875em;
    }
  }
`;

const Box30 = styled.div`
  height: 30px;
`;

/* === 크리스마스 눈 내리는 효과(유지) === */
const snowFall = keyframes`
  0% {
    transform: translateY(-10vh);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  100% {
    transform: translateY(110vh);
    opacity: 0;
  }
`;

const SnowLayer = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: 0;
`;

const Snowflake = styled.div<{
  left: number;
  duration: number;
  delay: number;
  size: number;
}>`
  position: absolute;
  top: -10vh;
  left: ${({ left }) => left}%;
  font-size: ${({ size }) => size}rem;
  animation: ${snowFall} ${({ duration }) => duration}s linear infinite;
  animation-delay: ${({ delay }) => delay}s;
`;

/** 0~100 clamp */
const clampPercent = (n: number) => Math.min(Math.max(n, 0), 100);
const intPercent = (n: number) => Math.floor(clampPercent(n));

/**
 * 연도 진행률 계산 (로컬 타임 기준)
 * - 2025년: 12/31 23:00부터 100%로 처리
 * - 2026년~: 해당 로컬 연도 시작~다음 해 시작까지 선형 진행률
 */
function getYearProgressLocal(now: Date): number {
  const year = now.getFullYear();
  const nowMs = now.getTime();

  const startOfYear = new Date(year, 0, 1, 0, 0, 0, 0).getTime();

  if (year === 2025) {
    const endAt23 = new Date(2025, 11, 31, 23, 0, 0, 0).getTime();
    const total = endAt23 - startOfYear;

    if (nowMs >= endAt23) return 100;

    const elapsed = nowMs - startOfYear;
    return clampPercent((elapsed / total) * 100);
  }

  const startOfNextYear = new Date(year + 1, 0, 1, 0, 0, 0, 0).getTime();
  const total = startOfNextYear - startOfYear;

  const elapsed = nowMs - startOfYear;
  return clampPercent((elapsed / total) * 100);
}

/** 월의 N번째 특정 요일 날짜 구하기 (로컬) */
function getNthWeekdayOfMonth(
  year: number,
  monthIndex: number, // 0-based
  weekday: number, // 0=Sun ... 6=Sat
  n: number
): Date {
  const first = new Date(year, monthIndex, 1, 0, 0, 0, 0);
  const firstWeekday = first.getDay();
  const offset = (weekday - firstWeekday + 7) % 7;
  const day = 1 + offset + (n - 1) * 7;
  return new Date(year, monthIndex, day, 0, 0, 0, 0);
}

/** 날짜(연/월/일)만 비교용 */
function ymdKey(d: Date) {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** 설날(음력 1월 1일~3일) 감지: Intl chinese calendar 지원 시 자동 */
function isLunarNewYearWindow(now: Date): boolean {
  try {
    // 일부 브라우저에서 RangeError 날 수 있음
    const fmt = new Intl.DateTimeFormat("en-u-ca-chinese", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });

    const parts = fmt.formatToParts(now);
    const monthStr = parts.find((p) => p.type === "month")?.value ?? "";
    const dayStr = parts.find((p) => p.type === "day")?.value ?? "";

    // "11bis" 같은 값도 있을 수 있어서 parseInt로 처리
    const lunarMonth = parseInt(monthStr, 10);
    const lunarDay = parseInt(dayStr, 10);

    return lunarMonth === 1 && lunarDay >= 1 && lunarDay <= 3;
  } catch {
    // Intl 미지원 환경이면 안전하게 false 처리(원하면 여기서 연도별 테이블 fallback 넣어줄게)
    return false;
  }
}

/** 명절 모드 결정 (로컬 기준) */
function getHolidayModeLocal(now: Date): HolidayMode {
  const year = now.getFullYear();
  const month = now.getMonth(); // 0=Jan
  const day = now.getDate();

  // 1) 크리스마스: 12/22 ~ 12/26 (기존 유지)
  if (month === 11 && day >= 22 && day <= 26) return "christmas";

  // 2) 1월 1일(신정): 1/1 ~ 1/3
  if (month === 0 && day >= 1 && day <= 3) return "newyear";

  // 3) 설날(음력): 음력 1월 1일~3일 (브라우저가 chinese calendar 지원하면 자동)
  if (isLunarNewYearWindow(now)) return "lunarnewyear";

  // 4) 추수감사절: (US) 11월 4번째 목요일 ~ 일요일
  const usThanksgiving = getNthWeekdayOfMonth(year, 10, 4, 4); // Nov(10), Thu(4), 4th
  const usThuKey = ymdKey(usThanksgiving);
  const nowKey = ymdKey(now);

  const usFri = new Date(usThanksgiving);
  usFri.setDate(usFri.getDate() + 1);

  const usSat = new Date(usThanksgiving);
  usSat.setDate(usSat.getDate() + 2);

  const usSun = new Date(usThanksgiving);
  usSun.setDate(usSun.getDate() + 3);

  const usWindow = new Set([
    usThuKey,
    ymdKey(usFri),
    ymdKey(usSat),
    ymdKey(usSun),
  ]);
  if (usWindow.has(nowKey)) return "thanksgiving";

  // (Canada) 10월 2번째 월요일(원하면 주말 포함으로 확장 가능)
  // const caThanksgiving = getNthWeekdayOfMonth(year, 9, 1, 2); // Oct(9), Mon(1), 2nd
  // if (nowKey === ymdKey(caThanksgiving)) return "thanksgiving";

  return "none";
}

function App() {
  const [currentDateTime, setCurrentDateTime] = useState("");
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [holidayMode, setHolidayMode] = useState<HolidayMode>("none");
  const [yearMode, setYearMode] = useState<YearMode>("y2025");
  const [currentYear, setCurrentYear] = useState<number>(2025);

  useEffect(() => {
    const update = () => {
      const now = new Date();
      const year = now.getFullYear();
      setCurrentYear(year);

      // 진행률
      const progressRaw = getYearProgressLocal(now);
      setProgressPercentage(progressRaw);

      // 날짜/시간 표시
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const dateOptions: Intl.DateTimeFormatOptions = {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        timeZone: tz,
      };
      const timeOptions: Intl.DateTimeFormatOptions = {
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        hour12: true,
        timeZone: tz,
      };

      const formattedDate = new Intl.DateTimeFormat("en-GB", dateOptions)
        .format(now)
        .replace(/\//g, " . ");
      const formattedTime = new Intl.DateTimeFormat(
        "en-US",
        timeOptions
      ).format(now);

      setCurrentDateTime(`${formattedDate} | ${formattedTime}`);

      // ✅ 명절 모드 자동
      setHolidayMode(getHolidayModeLocal(now));

      // 연도 모드(명언 변경용)
      if (year >= 2026) setYearMode("y2026plus");
      else setYearMode("y2025");
    };

    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  const getProgressBarColor = (progress: number): string => {
    if (progress < 50) return "#8fa5f5";
    if (progress < 90) return "#e89819";
    return "#e84d19";
  };

  const renderTitle = () => {
    if (holidayMode === "christmas") return "🎄 Merry Christmas";
    if (holidayMode === "newyear") return "🎉 Happy New Year";
    if (holidayMode === "lunarnewyear") return "🧧 Happy Lunar New Year";
    if (holidayMode === "thanksgiving") return "🦃 Happy Thanksgiving";
    return `⏳ ${currentYear} in Progress...`;
  };

  const renderQuote = () => {
    if (holidayMode === "christmas") {
      return (
        <>
          Warm wishes <br />
          for peace and joy.
        </>
      );
    }
    if (holidayMode === "newyear") {
      return (
        <>
          New year, <br />
          new momentum.
        </>
      );
    }
    if (holidayMode === "lunarnewyear") {
      return (
        <>
          May your year <br />
          be full of luck and growth.
        </>
      );
    }
    if (holidayMode === "thanksgiving") {
      return (
        <>
          Grateful for today, <br />
          ready for tomorrow.
        </>
      );
    }

    // 기본 연도별 명언
    if (yearMode === "y2026plus") {
      return (
        <>
          The best way to predict <br />
          the future is to create it.
          <br />- Peter Drucker -
        </>
      );
    }

    return (
      <>
        Don’t watch the clock, <br />
        Do what it does. Keep going.
        <br />- Sam Levenson -
      </>
    );
  };

  const snowflakes = useMemo(
    () =>
      Array.from({ length: 20 }).map((_, i) => (
        <Snowflake
          key={i}
          left={(i * 5.3) % 100}
          duration={8 + (i % 5)}
          delay={(i * 0.7) % 6}
          size={0.7 + (i % 4) * 0.2}
        >
          ❄️
        </Snowflake>
      )),
    []
  );

  const barColor = getProgressBarColor(progressPercentage);
  const progressDisplay = intPercent(progressPercentage);

  return (
    <>
      <GlobalStyle />
      <ResponsiveStyle />

      <Container holidayMode={holidayMode}>
        {/* ✅ 폭죽은 제거했고, 크리스마스 눈만 유지 */}
        {holidayMode === "christmas" && <SnowLayer>{snowflakes}</SnowLayer>}

        <Content>
          <Title>{renderTitle()}</Title>
          <Title2>{renderQuote()}</Title2>
          <Box30 />
          <DateTime>{currentDateTime}</DateTime>

          <ProgressContainer>
            <ProgressBar width={`${progressPercentage}%`} color={barColor} />
            <ProgressText>{progressDisplay}%</ProgressText>
          </ProgressContainer>

          <InstagramLink
            href="https://instagram.com/progress_of_years"
            target="_blank"
            rel="noopener noreferrer"
          >
            <InstagramIcon />
            progress_of_years
          </InstagramLink>
        </Content>
      </Container>
    </>
  );
}

export default App;
